import { MANUAL_ENTRY_ROW_VALUE } from '@server/types';

import type { Question, QuestionAnswerEntry } from '@/_shared/types';

// Pure selection→answer helpers shared by the single/multi lists, the standalone question cards, and
// the wizard. Kept in a non-component module so the list components stay Fast-Refresh-friendly.

/**
 * Sentinel radio value for the "Other" free-text row. Comes from the backend schema, which strips any
 * authored option carrying this label — so the two sides cannot drift and the reservation is enforced
 * where payloads are validated, not merely made unlikely by a leading space.
 */
export const OTHER_VALUE = MANUAL_ENTRY_ROW_VALUE;

/** Defaults filtered to actual option labels — a default matching no option must not seed a phantom
 * selection that's invisible yet counts toward the answer / min-max / Submit. */
export function validDefaults(question: Question): string[] {
  const labels = new Set(question.options.map((o) => o.label));
  return (question.defaults ?? []).filter((d) => labels.has(d));
}

/** Machine identities for the selected labels — each option's `value`; a free-text "Other" (which has
 * no matching option) is its own value. */
function optionValues(question: Question, labels: string[]): string[] {
  const byLabel = new Map(question.options.map((o) => [o.label, o.value]));
  return labels.map((l) => byLabel.get(l) ?? l);
}

/** Build the answer entry for a single-select selection, or null when the selection is invalid/absent. */
export function buildSingleEntry(
  question: Question,
  value: string | null,
  otherText: string,
): QuestionAnswerEntry | null {
  if (value === null) return null;
  if (value === OTHER_VALUE) {
    const text = otherText.trim();
    return text ? { questionId: question.id, kind: 'manual', text } : null;
  }
  return { questionId: question.id, kind: 'selected', labels: [value], values: optionValues(question, [value]) };
}

export function multiTotal(checked: Set<string>, otherChecked: boolean): number {
  return checked.size + (otherChecked ? 1 : 0);
}

/** Why Submit/Next is disabled for a single-select — null when the answer is submittable.
 * Phrased without a trailing verb: the same string labels a wizard's "Next" and a card's "Submit". */
export function singleSubmitDisabledReason(value: string | null, otherText: string): string | null {
  if (value === null) return 'Pick an option';
  if (value === OTHER_VALUE && otherText.trim().length === 0) return 'Enter your own answer';
  return null;
}

/** Why Submit/Next is disabled for a multi-select — null when the answer is submittable. */
export function multiSubmitDisabledReason(
  question: Question,
  checked: Set<string>,
  otherChecked: boolean,
  otherText: string,
): string | null {
  if (otherChecked && otherText.trim().length === 0) return 'Enter your own answer';
  const total = multiTotal(checked, otherChecked);
  const min = question.min ?? 1;
  if (total < min) {
    return min === 1 ? 'Pick at least one option' : `Select at least ${min} options`;
  }
  if (question.max != null && total > question.max) {
    return `Select at most ${question.max} options`;
  }
  return null;
}

/** Build the answer entry for a multi-select: selected OPTION labels/values, with any "Other" free text
 * kept DISTINCT in `otherText` (never inlined into labels, so it is never coerced to an option's value). */
export function buildMultiEntry(
  question: Question,
  checked: Set<string>,
  otherChecked: boolean,
  otherText: string,
): Extract<QuestionAnswerEntry, { kind: 'selected' }> {
  const labels = [...checked];
  const trimmed = otherChecked ? otherText.trim() : '';
  return {
    questionId: question.id,
    kind: 'selected',
    labels,
    values: optionValues(question, labels),
    ...(trimmed ? { otherText: trimmed } : {}),
  };
}
